import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HeaderNav } from "@/components/HeaderNav";
import { DimensionBar } from "@/components/DimensionBar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, FlaskConical, Rocket, ListChecks, AlertTriangle } from "lucide-react";

const SCALE = [
  { v: 1, label: "Strongly disagree" }, { v: 2, label: "Disagree" },
  { v: 3, label: "Neither agree nor disagree" }, { v: 4, label: "Agree" }, { v: 5, label: "Strongly agree" },
];

export default function AdminPreview() {
  const { version } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [responses, setResponses] = useState({});
  const [idx, setIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [dims, setDims] = useState([]);
  const [versionMeta, setVersionMeta] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/items?version=${version}`);
      setItems(r.data.items.filter((i) => i.active));
      const d = await api.get("/admin/dimensions");
      setDims(d.data.dimensions);
      const vs = await api.get("/admin/versions");
      setVersionMeta((vs.data.versions || []).find((v) => v.version === version) || null);
      const ck = await api.get(`/admin/versions/${version}/checklist`);
      setChecklist(ck.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [version]);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post(`/admin/versions/${version}/publish`);
      toast.success("Version published & activated");
      navigate("/admin");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPublishing(false); }
  };

  const current = items[idx];
  const total = items.length;
  const answered = Object.keys(responses).length;

  const choose = (v) => {
    if (!current) return;
    setResponses((r) => ({ ...r, [current.id]: v }));
    if (idx < total - 1) setTimeout(() => setIdx((i) => i + 1), 150);
  };

  const runScore = async () => {
    try {
      const r = await api.post("/admin/test-scoring", { version, responses });
      setResult(r.data.result);
      toast.success("Deterministic scoring complete");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return (<div><HeaderNav /><div className="max-w-2xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading draft…</div></div>);

  const nameByKey = {};
  dims.forEach((d) => { nameByKey[d.key] = d.name; });

  return (
    <div>
      <HeaderNav />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-10 pb-24">
        <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-brass mb-4"><ArrowLeft className="w-4 h-4" /> Back to configuration</button>
        <div className="flex items-center gap-2 mb-2"><FlaskConical className="w-5 h-5 text-brass" /><p className="cg-eyebrow">Draft preview &amp; scoring test</p></div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#F3F4F6] mb-2">Version {version}</h1>
        <p className="text-sm text-[#9CA3AF] mb-8">Answer the items to sanity-check wording, then run the deterministic scoring engine. This is a preview — nothing is stored as a real assessment or counted in any aggregate.</p>

        {checklist && (
          <div className="cg-card p-6 mb-8" data-testid="publish-checklist">
            <div className="flex items-center gap-2 mb-1"><ListChecks className="w-5 h-5 text-brass" /><h3 className="font-serif text-xl text-[#F3F4F6]">Pre-publish checklist</h3></div>
            <p className="text-xs text-[#6B7280] font-mono mb-4">{checklist.total_active} active items · {checklist.balanced ? "balanced across dimensions" : "review balance below"}</p>
            <div className="space-y-1 mb-4">
              {checklist.dimensions.map((r) => (
                <div key={r.key} className="flex justify-between text-xs font-mono border-b border-[var(--cg-border)] py-1.5">
                  <span className="text-[#9CA3AF]">{r.name}</span>
                  <span className="text-[#E8EAEE]">{r.active} active{r.inactive ? ` · ${r.inactive} inactive` : ""} · {r.reverse} reverse</span>
                </div>
              ))}
            </div>
            {checklist.warnings.length === 0 ? (
              <p className="inline-flex items-center gap-2 text-sm text-[#7D9D86]" data-testid="checklist-ok"><Check className="w-4 h-4" /> Balanced and ready to publish.</p>
            ) : (
              <div className="space-y-1" data-testid="checklist-warnings">
                {checklist.warnings.map((w, i) => (
                  <p key={i} className="inline-flex items-start gap-2 text-sm text-amber2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {w}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {!result ? (
          <>
            <div className="mb-8">
              <div className="flex justify-between mb-2"><span className="cg-eyebrow">Item {Math.min(idx + 1, total)} of {total}</span><span className="cg-eyebrow">{answered}/{total} answered</span></div>
              <div className="h-1 rounded-full bg-[#222634] overflow-hidden"><div className="h-full bg-brass rounded-full transition-all duration-500" style={{ width: `${total ? (answered / total) * 100 : 0}%` }} /></div>
            </div>
            {current && (
              <div key={current.id} className="cg-fade">
                <p className="cg-eyebrow mb-2">{current.item_id} · {nameByKey[current.dimension] || current.dimension}{current.reverse_scored && " · reverse"}</p>
                <h2 className="font-serif text-2xl sm:text-3xl leading-snug text-[#F3F4F6] mb-8 min-h-[4rem]" data-testid="preview-item-text">{current.item_text}</h2>
                <div className="space-y-3">
                  {SCALE.map((s) => {
                    const sel = responses[current.id] === s.v;
                    return (
                      <button key={s.v} data-testid={`preview-scale-${s.v}`} onClick={() => choose(s.v)}
                        className={`cg-scale-btn w-full flex items-center gap-4 px-5 py-4 rounded-xl border text-left ${sel ? "border-brass bg-[rgba(217,162,95,0.10)]" : "border-[var(--cg-border)] bg-[#1A1D27] hover:border-[rgba(217,162,95,0.4)]"}`}>
                        <span className={`font-mono text-sm w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${sel ? "bg-brass text-obsidian" : "bg-[#222634] text-[#9CA3AF]"}`}>{sel ? <Check className="w-3.5 h-3.5" /> : s.v}</span>
                        <span className={sel ? "text-[#F3F4F6]" : "text-[#9CA3AF]"}>{s.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="mt-8 flex items-center justify-between gap-3">
              <button disabled={idx === 0} onClick={() => setIdx((i) => i - 1)} className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-brass disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Previous</button>
              <div className="flex items-center gap-3">
                {answered >= 1 && (
                  <button data-testid="preview-score-button" onClick={runScore}
                    className="inline-flex items-center gap-2 bg-brass text-obsidian font-medium px-6 py-2.5 rounded-full cg-scale-btn hover:bg-amber2">
                    Run scoring test ({answered})
                  </button>
                )}
                {idx < total - 1 && (
                  <button onClick={() => setIdx((i) => i + 1)} className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-brass">Next <ArrowRight className="w-4 h-4" /></button>
                )}
              </div>
            </div>
            {answered < total && <p className="mt-4 text-xs text-[#6B7280] text-right">Answer all {total} items for a complete result, or score partially anytime.</p>}
          </>
        ) : (
          <div className="cg-fade-up" data-testid="preview-result">
            <div className="cg-card p-6 mb-5">
              <h2 className="font-serif text-2xl text-[#F3F4F6] mb-1">Dimension results</h2>
              <p className="text-xs text-[#6B7280] font-mono mb-4">scoring {result.scoring_version} · version {version} · preview only</p>
              {dims.map((d) => result.dimension_scores[d.key] && (
                <DimensionBar key={d.key} dimKey={d.key} name={d.name} aligned={result.dimension_scores[d.key].aligned_mean} band={result.dimension_scores[d.key].band} testId={`preview-dim-${d.key}`} />
              ))}
            </div>
            <div className="cg-card p-6 mb-5">
              <p className="cg-eyebrow mb-2">Calculations</p>
              {Object.values(result.dimension_scores).map((d) => (
                <div key={d.dimension} className="flex justify-between text-xs font-mono border-b border-[var(--cg-border)] py-1.5"><span className="text-[#9CA3AF]">{d.name}</span><span className="text-[#E8EAEE]">raw {d.raw_mean} · aligned {d.aligned_mean} · {d.band} · n{d.n_items}</span></div>
              ))}
              {result.archetype && <p className="mt-3 text-sm text-[#9CA3AF]">Archetype match: <span className="text-brass">{result.archetype.name}</span></p>}
            </div>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => { setResult(null); setResponses({}); setIdx(0); }} className="border border-[var(--cg-border)] text-sm px-5 py-2.5 rounded-full hover:border-brass cg-scale-btn text-[#9CA3AF]">Test again</button>
              {versionMeta && !versionMeta.published && (
                <button data-testid="preview-publish-button" onClick={publish} disabled={publishing}
                  className="inline-flex items-center gap-2 bg-brass text-obsidian text-sm font-medium px-6 py-2.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-40">
                  <Rocket className="w-4 h-4" /> {publishing ? "Publishing…" : "Publish & go live"}
                </button>
              )}
              {versionMeta && versionMeta.published && (
                <span className="inline-flex items-center gap-2 text-sm text-[#7D9D86] px-3 py-2.5" data-testid="preview-published-note"><Check className="w-4 h-4" /> Already published (immutable)</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
